import type { GroupedPoll, Poll, PollGroup, PollOption, PollResponse } from '@tunasong/schemas'

/**
 * Every rule of a grouped poll — a many-of-many poll whose options belong to groups, each with its own quota. Kept
 * pure so the vote, finalize and editor views cannot disagree about what a group contains or when it is full.
 *
 * A live option outside every live group — no groupId, or one pointing at a missing or removed group — belongs to
 * the first live group. Nothing vanishes: "Add group" assigns every option to the first group anyway, so a groupId
 * that never got saved (an API on an older schema strips unknown nested fields) reads exactly as it was written.
 */

type Choices = Record<string, boolean>

export const getLiveGroups = (poll: GroupedPoll) => poll.pollGroups.filter(group => !group.removed)

export const getGroupQuota = (group: PollGroup) => group.quota ?? 1

const isLiveGroup = (poll: GroupedPoll, groupId: string) => getLiveGroups(poll).some(group => group.id === groupId)

/** The live group an option belongs to: its own, or the first live group when its own is missing or removed. */
const resolveGroupId = (poll: GroupedPoll, option: PollOption) =>
  option.groupId && isLiveGroup(poll, option.groupId) ? option.groupId : getLiveGroups(poll)[0]?.id

export const getGroupOptionIndices = (poll: GroupedPoll, groupId: string) =>
  isLiveGroup(poll, groupId)
    ? poll.pollOptions.flatMap((option, idx) =>
        !option.removed && resolveGroupId(poll, option) === groupId ? [idx] : []
      )
    : []

/**
 * The quota the group can actually be held to: never more than its live options (a typed 12, or options removed
 * after the quota was set), never less than 1. Every rule that a quota drives uses this, not the stored value.
 */
export const getEffectiveQuota = (poll: GroupedPoll, group: PollGroup) =>
  Math.max(1, Math.min(getGroupQuota(group), getGroupOptionIndices(poll, group.id).length))

export const getOptionGroup = (poll: GroupedPoll, idx: number) => {
  const option = poll.pollOptions[idx]
  if (!option || option.removed) {
    return undefined
  }
  const groupId = resolveGroupId(poll, option)
  return getLiveGroups(poll).find(group => group.id === groupId)
}

/** Groups a voter can answer: live, with at least one live option. */
export const getVotableGroups = (poll: GroupedPoll) =>
  getLiveGroups(poll).filter(group => getGroupOptionIndices(poll, group.id).length > 0)

export const countGroupPicks = (poll: GroupedPoll, choices: Choices, groupId: string) =>
  getGroupOptionIndices(poll, groupId).filter(idx => choices[idx]).length

/**
 * The choices after picking (or unpicking) `idx`. Quota 1 moves the pick, like a radio — and so does any group
 * whose quota was lowered under the voter's existing picks, which brings them back to exactly one. A full group
 * with a larger quota refuses the pick; the view disables those options.
 */
export const pickInGroup = (poll: GroupedPoll, choices: Choices, idx: number, value: boolean): Choices => {
  if (!value) {
    return { ...choices, [idx]: false }
  }
  const group = getOptionGroup(poll, idx)
  if (!group) {
    return choices
  }
  const quota = getEffectiveQuota(poll, group)
  const others = getGroupOptionIndices(poll, group.id).filter(other => other !== idx && choices[other])
  if (quota === 1) {
    return { ...choices, ...Object.fromEntries(others.map(other => [other, false])), [idx]: true }
  }
  if (others.length >= quota) {
    return choices
  }
  return { ...choices, [idx]: true }
}

/** Every votable group has exactly its quota. Over quota (a lowered quota) is not complete. */
export const isCompleteGroupedResponse = (poll: GroupedPoll, choices: Choices) => {
  const groups = getVotableGroups(poll)
  return (
    groups.length > 0 &&
    groups.every(group => countGroupPicks(poll, choices, group.id) === getEffectiveQuota(poll, group))
  )
}

/** Responses that picked anything in the group: the denominator for that group's percentages. */
export const getGroupVoters = (poll: GroupedPoll, responses: PollResponse[], groupId: string) => {
  const indices = getGroupOptionIndices(poll, groupId)
  return responses.filter(response => indices.some(idx => response.choices?.[idx]))
}

export const getGroupVoteCounts = (poll: GroupedPoll, responses: PollResponse[], groupId: string) =>
  new Map(
    getGroupOptionIndices(poll, groupId).map(idx => [idx, responses.filter(response => response.choices?.[idx]).length])
  )

export const getGroupProgress = (poll: GroupedPoll, responses: PollResponse[], idx: number) => {
  const group = getOptionGroup(poll, idx)
  if (!group) {
    return 0
  }
  const voters = getGroupVoters(poll, responses, group.id)
  if (voters.length === 0) {
    return 0
  }
  return (voters.filter(response => response.choices?.[idx]).length / voters.length) * 100
}

/** Most votes first; ties keep stored order, so a lightly voted group does not look shuffled. */
export const rankGroupOptions = (poll: GroupedPoll, responses: PollResponse[], groupId: string) => {
  const counts = getGroupVoteCounts(poll, responses, groupId)
  return [...counts.keys()].sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a - b)
}

/**
 * The owner's starting point at finalize: the top `quota` options of each group that got any votes. When the last
 * place taken shares its count with an option left out, the group is reported in `ties` with every option at that
 * count, so the view can say the pick was arbitrary.
 */
export const getDefaultGroupWinners = (poll: GroupedPoll, responses: PollResponse[]) => {
  const finalOption: Choices = {}
  const ties = new Map<string, number[]>()

  for (const group of getVotableGroups(poll)) {
    const counts = getGroupVoteCounts(poll, responses, group.id)
    const ranked = rankGroupOptions(poll, responses, group.id).filter(idx => (counts.get(idx) ?? 0) > 0)
    const winners = ranked.slice(0, getEffectiveQuota(poll, group))
    winners.forEach(idx => {
      finalOption[idx] = true
    })
    const cutoff = winners.at(-1)
    const cutoffCount = cutoff === undefined ? 0 : (counts.get(cutoff) ?? 0)
    const tied = ranked.filter(idx => counts.get(idx) === cutoffCount)
    if (cutoff !== undefined && tied.some(idx => !winners.includes(idx))) {
      ties.set(group.id, tied)
    }
  }
  return { finalOption, ties }
}

export const formatGroupStatus = (picked: number, quota: number) => {
  if (picked === quota) {
    return quota === 1 ? '✓ Picked' : `✓ Picked ${quota}`
  }
  return picked === 0 ? `Pick ${quota}` : `Picked ${picked} of ${quota}`
}

export const newGroup = (name: string): PollGroup => ({ id: crypto.randomUUID(), name })

/**
 * The first group turns a plain poll into a grouped one: every live ungrouped option joins it, so a grouped poll
 * never has stray options. Indices do not move, so answers already given still count. The poll-wide quota moves
 * onto that first group (capped at its live options), so answers given under it stay complete; after that each
 * group has its own.
 */
export const addGroup = (poll: Poll, group: PollGroup): Poll => {
  const isFirst = (poll.pollGroups ?? []).length === 0
  if (!isFirst) {
    return { ...poll, pollGroups: [...(poll.pollGroups ?? []), group] }
  }
  const liveOptions = poll.pollOptions.filter(option => !option.removed && !option.groupId).length
  const responseQuota = 'responseQuota' in poll ? poll.responseQuota : undefined
  const quota = typeof responseQuota === 'number' ? Math.max(1, Math.min(responseQuota, liveOptions)) : 1
  return {
    ...poll,
    responseQuota: undefined,
    pollGroups: [quota > 1 ? { ...group, quota } : group],
    pollOptions: poll.pollOptions.map(option =>
      option.removed || option.groupId ? option : { ...option, groupId: group.id }
    ),
  }
}

export const updateGroup = (poll: Poll, groupId: string, patch: Partial<Pick<PollGroup, 'name' | 'quota'>>): Poll => ({
  ...poll,
  pollGroups: (poll.pollGroups ?? []).map(group => (group.id === groupId ? { ...group, ...patch } : group)),
})

/** Tombstones the group and its options. Never splices: answers are keyed by option index. */
export const removeGroup = (poll: Poll, groupId: string): Poll => ({
  ...poll,
  pollGroups: (poll.pollGroups ?? []).map(group => (group.id === groupId ? { ...group, removed: true } : group)),
  pollOptions: poll.pollOptions.map(option => (option.groupId === groupId ? { ...option, removed: true } : option)),
})

export const addOptionToGroup = (poll: Poll, groupId: string): Poll => ({
  ...poll,
  pollOptions: [...poll.pollOptions, { name: '', groupId }],
})

/** Moves an option to another group. Its index is unchanged, so every answer for it moves with it. */
export const moveOptionToGroup = (poll: Poll, idx: number, groupId: string): Poll => ({
  ...poll,
  pollOptions: poll.pollOptions.map((option, i) => (i === idx ? { ...option, groupId } : option)),
})

/** Adds `group` at the end and moves the option into it, in one write. */
export const moveOptionToNewGroup = (poll: Poll, idx: number, group: PollGroup): Poll =>
  moveOptionToGroup(addGroup(poll, group), idx, group.id)
